import { MouseEvent, ReactNode, Ref } from 'react';
import styled from 'styled-components';
import { ReactComponent as ArrowIcon } from '@elementary/common-ui/assets/arrow-down-expand.svg';
import { Flex, FlexCenter } from '@elementary/common-ui/components';
import { CircularProgress, Typography } from '@mui/material';
import { GRAPH_CHILD_NODE_HEIGHT } from './constants';

const Row = styled(Flex)<{
  $width: number;
  $borderColor: string;
  $isLast: boolean;
  $isIndented: boolean;
  $isOnHighlightPath: boolean;
  $filterBackground?: string;
}>`
  box-sizing: border-box;
  width: ${({ $width }) => $width}px;
  height: ${GRAPH_CHILD_NODE_HEIGHT}px;
  align-items: center;
  gap: 10px;
  padding: 0 20px 0 ${({ $isIndented }) => ($isIndented ? 48 : 20)}px;
  cursor: pointer;
  pointer-events: auto;
  background: ${({ theme, $isOnHighlightPath, $filterBackground }) =>
    $filterBackground ??
    ($isOnHighlightPath
      ? theme.palette.common.gray9
      : theme.palette.common.white)};
  border: 3px solid ${({ $borderColor }) => $borderColor};
  border-top: none;
  ${({ $isLast }) =>
    $isLast ? 'border-radius: 0 0 20px 20px;' : 'border-bottom: none;'}

  &:hover {
    background: ${({ theme }) => theme.palette.common.gray10};
  }
`;

const RowLabel = styled(Typography)<{ $isHighlighted: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 22px;
  line-height: normal;
  font-weight: ${({ $isHighlighted }) => ($isHighlighted ? 700 : 400)};
  color: ${({ theme }) => theme.palette.common.black};
`;

type CompactPanelRowProps = {
  rowRef?: Ref<HTMLDivElement>;
  width: number;
  borderColor: string;
  label: string;
  isLast: boolean;
  isIndented?: boolean;
  isHighlighted: boolean;
  isOnHighlightPath: boolean;
  filterBackground?: string;
  onContextMenu: (e: MouseEvent) => void;
  children?: ReactNode;
};

export const CompactPanelRow = ({
  rowRef,
  width,
  borderColor,
  label,
  isLast,
  isIndented = false,
  isHighlighted,
  isOnHighlightPath,
  filterBackground,
  onContextMenu,
  children,
}: CompactPanelRowProps) => (
  <Row
    ref={rowRef}
    $width={width}
    $borderColor={borderColor}
    $isLast={isLast}
    $isIndented={isIndented}
    $isOnHighlightPath={isOnHighlightPath}
    $filterBackground={filterBackground}
    onContextMenu={onContextMenu}
  >
    <RowLabel noWrap $isHighlighted={isHighlighted}>
      {label}
    </RowLabel>
    {children}
  </Row>
);

const ExpandButton = styled(FlexCenter)<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;
  cursor: pointer;
`;

const ExpandArrow = styled(ArrowIcon)<{ $flip: boolean; $width: number }>`
  width: ${({ $width }) => $width}px;
  transform: ${({ $flip }) => ($flip ? 'rotate(-180deg)' : undefined)};
  transition: transform 0.1s;
`;

type CompactExpandButtonProps = {
  size: number;
  isLoading: boolean;
  isExpanded: boolean;
};

export const CompactExpandButton = ({
  size,
  isLoading,
  isExpanded,
}: CompactExpandButtonProps) => (
  <ExpandButton id="expand-button" $size={size}>
    {isLoading ? (
      <CircularProgress size={size * 0.7} />
    ) : (
      <ExpandArrow $flip={isExpanded} $width={size * 0.7} />
    )}
  </ExpandButton>
);
